import React, { useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteAccount } from "../../services/account";
import { useAuth } from "../../context/AuthContext";

export default function AccountDeletionSection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (window.location.hash !== "#excluir-conta") {
      return;
    }
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleDelete = async () => {
    if (!password.trim()) {
      toast.error("Informe sua senha para confirmar a exclusão.");
      return;
    }
    if (!confirmed) {
      toast.error(
        "Marque a confirmação de que entende que a exclusão é permanente.",
      );
      return;
    }

    const ok = window.confirm(
      "Tem certeza absoluta? Sua conta e os dados associados serão excluídos permanentemente. Esta ação não pode ser desfeita.",
    );
    if (!ok) {
      return;
    }

    setDeleting(true);
    try {
      await deleteAccount(password);
      toast.success("Conta excluída com sucesso.");
      logout();
      navigate("/login");
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(
        err?.response?.data?.error ||
          "Não foi possível excluir a conta. Tente novamente.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      id="excluir-conta"
      className="mt-8 rounded-xl border border-red-200 bg-red-50/80 p-4 dark:border-red-900/50 dark:bg-red-950/20 sm:p-6"
    >
      <div className="flex items-center gap-2">
        <FiAlertTriangle className="text-red-600 dark:text-red-400" size={22} />
        <h3 className="text-lg font-semibold text-red-700 dark:text-red-300">
          Excluir conta
        </h3>
      </div>
      <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
        Ao excluir sua conta, seus dados de perfil, agenda, alunos vinculados e
        demais informações associadas serão removidos de forma permanente. Esta
        ação não pode ser desfeita.
      </p>

      <label className="mt-4 flex items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-1"
        />
        <span>
          Entendo que a exclusão é permanente e que não poderei recuperar minha
          conta depois.
        </span>
      </label>

      <div className="mt-4">
        <label
          htmlFor="delete-account-password"
          className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-200"
        >
          Senha atual
        </label>
        <input
          id="delete-account-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-black outline-none focus:border-red-500 dark:border-strokedark dark:bg-boxdark dark:text-white"
          placeholder="Digite sua senha para confirmar"
        />
      </div>

      <button
        type="button"
        onClick={handleDelete}
        disabled={deleting}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        <FiTrash2 size={16} />
        {deleting ? "Excluindo conta..." : "Excluir minha conta permanentemente"}
      </button>
    </section>
  );
}
